/**
 * 顶栏（架构 §2 src/components/layout/TopBar.tsx / T12 / T15 / T16）
 *  - 根据当前路由显示标题：会话列表/用户中心/邀请码管理/聊天（会话名）
 *  - 聊天页标题：群聊用 name；私聊用对端用户名（未知时回落"私聊"）
 *  - U2 §5.2：右上角语言切换器（🌐）
 */
import { useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useChatStore } from '../../store/chat.store';
import { useAuthStore } from '../../store/auth.store';
import { useUserStore } from '../../store/user.store';
import { isTrue } from '../../types/models';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageSwitcher from '../language/LanguageSwitcher';
import ThemeToggleButton from '../profile/ThemeToggleButton';

function titleFor(pathname: string): string {
  if (pathname.startsWith('/user')) return '用户中心';
  if (pathname.startsWith('/invites')) return '邀请码管理';
  if (pathname.startsWith('/c/')) return '聊天';
  return '知会 ZhiHui';
}

export default function TopBar({
  showMenu = false,
  onMenuClick,
}: {
  showMenu?: boolean;
  onMenuClick?: () => void;
}) {
  const location = useLocation();
  const conversations = useChatStore((s) => s.conversations);
  const members = useChatStore((s) => s.members);
  const me = useAuthStore((s) => s.user);
  const knownUsers = useUserStore((s) => s.knownUsers);

  let title = titleFor(location.pathname);

  // 聊天页：解析会话名
  const m = location.pathname.match(/^\/c\/(\d+)/);
  if (m) {
    const id = Number(m[1]);
    const conv = conversations.find((c) => c.id === id);
    if (conv) {
      if (conv.type === 'group') {
        title = conv.name || '群聊';
      } else {
        // 私聊：找对端成员
        const list = members[id] ?? [];
        const peer = list.find((mem) => mem.user_id !== me?.id);
        const peerUser = peer ? knownUsers[peer.user_id] : undefined;
        title = peerUser?.username ? `私聊 · ${peerUser.username}` : '私聊';
      }
    }
  }

  return (
    <AppBar
      position="static"
      color="inherit"
      sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Toolbar variant="dense">
        {showMenu && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 1 }} aria-label="打开菜单">
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {m && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
            {isTrue(conversations.find((c) => c.id === Number(m[1]))?.is_ai_member)
              ? '知会已加入'
              : ''}
          </Typography>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <ThemeToggleButton />
          <LanguageSwitcher />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
